import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { X, Image as ImageIcon, Upload } from 'lucide-react';

const AdminClassForm = ({
  formData,
  setFormData,
  editingClass,
  locations,
  uploading,
  fileInputRef,
  defaultImages,
  onSubmit,
  onOpenImageLibrary,
  onImageUpload,
  onReset
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background-paper border border-white/10 rounded-sm p-8 mb-12"
    >
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-bold">
          {editingClass ? 'EDIT CLASS' : 'CREATE NEW CLASS'}
        </h2>
        <Button variant="ghost" onClick={onReset} data-testid="close-form-btn">
          <X className="w-5 h-5" />
        </Button>
      </div>

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="title">Class Title *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="bg-background border-white/10"
              data-testid="class-title-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="instructor">Instructor *</Label>
            <Input
              id="instructor"
              value={formData.instructor}
              onChange={(e) => setFormData({ ...formData, instructor: e.target.value })}
              required
              className="bg-background border-white/10"
              data-testid="class-instructor-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="location">Location *</Label>
            <select
              id="location"
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              required
              className="w-full bg-background border border-white/10 rounded-sm p-2 text-white"
              data-testid="class-location-input"
            >
              {locations.map(loc => (
                <option key={loc.location_id} value={loc.name}>{loc.name}</option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="datetime">Date & Time *</Label>
            <Input
              id="datetime"
              type="datetime-local"
              value={formData.class_datetime}
              onChange={(e) => setFormData({ ...formData, class_datetime: e.target.value })}
              required
              className="bg-background border-white/10"
              data-testid="class-datetime-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="duration">Duration (minutes) *</Label>
            <Input
              id="duration"
              type="number"
              value={formData.duration}
              onChange={(e) => setFormData({ ...formData, duration: parseInt(e.target.value) })}
              required
              min="15"
              className="bg-background border-white/10"
              data-testid="class-duration-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="capacity">Capacity *</Label>
            <Input
              id="capacity"
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: parseInt(e.target.value) })}
              required
              min="1"
              className="bg-background border-white/10"
              data-testid="class-capacity-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="price">Price (£) *</Label>
            <Input
              id="price"
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
              required
              min="0"
              className="bg-background border-white/10"
              data-testid="class-price-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description *</Label>
          <Textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            required
            rows={4}
            className="bg-background border-white/10"
            data-testid="class-description-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="image_url">Class Image *</Label>
          <div className="flex gap-2">
            <Input
              id="image_url"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              required
              className="bg-background border-white/10 flex-1"
              placeholder="https://... or upload an image"
              data-testid="class-image-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenImageLibrary('form')}
              className="border-white/20"
              data-testid="open-library-btn"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Library
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              className="border-white/20"
              disabled={uploading}
              data-testid="upload-image-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Uploading...' : 'Upload'}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onImageUpload}
              className="hidden"
            />
          </div>
          {formData.image_url && (
            <div className="mt-2">
              <img
                src={formData.image_url}
                alt="Preview"
                className="w-32 h-32 object-cover rounded border border-white/10"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          <div className="flex gap-2 mt-2">
            <p className="text-xs text-neutral-500 mb-1">Quick select:</p>
          </div>
          <div className="flex gap-2">
            {defaultImages.map((url) => (
              <img
                key={url}
                src={url}
                alt={`Template image`}
                className="w-16 h-16 object-cover rounded cursor-pointer border-2 border-transparent hover:border-primary"
                onClick={() => setFormData({ ...formData, image_url: url })}
              />
            ))}
          </div>
        </div>

        <div className="flex gap-4">
          <Button type="submit" className="bg-primary hover:bg-primary-hover font-bold" data-testid="save-class-btn">
            {editingClass ? 'UPDATE CLASS' : 'CREATE CLASS'}
          </Button>
          <Button type="button" variant="outline" onClick={onReset} data-testid="cancel-form-btn">
            CANCEL
          </Button>
        </div>
      </form>
    </motion.div>
  );
};

export default AdminClassForm;
